// Zählplan (Belegart 'stock_count' mit Ergänzung stock_count_ext): planen, lesen, starten, abbrechen.
//
//   Geplant → Zählung läuft → In Prüfung → Gebucht (bzw. Abgeschlossen, wenn nichts abweicht) · Abgebrochen
//
// Beim Start hält die Inventur den Buchbestand ihres Umfangs als Momentaufnahme fest. Das Lager arbeitet weiter:
// Jede Zählung merkt sich den Buchbestand ihres Zeitpunkts, die Differenz ist „gezählt − Buchbestand beim Zählen“.
import { PERMISSION_COSTS_READ, defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { listInput, listOutput } from '@shared/list'
import { z } from 'zod'
import { COUNT_KINDS, COUNT_STATUSES, CREATE, READ, zDay, zItemRef, zLocationRef, zPerson, zProgress, zReason } from './common'

const zZone = z.string().trim().min(1).max(60)

export const zCountView = z.object({
  id: zId,
  /** Belegnummer – erst mit dem Buchen vergeben */
  number: z.string().nullable(),
  name: z.string(),
  kind: z.enum(COUNT_KINDS),
  kindLabel: z.string(),
  status: z.enum(COUNT_STATUSES),
  statusLabel: z.string(),
  location: zLocationRef,
  /** Stichtag: Zu diesem Tag werden die Differenzen gebucht */
  keyDate: z.string(),
  blind: z.boolean(),
  /** Umfang ist für andere Bestandsbuchungen gesperrt, solange gezählt wird */
  lockScope: z.boolean(),
  /** Umfang in einem Satz: „Zonen Gang, Reserve · alle Artikel“ */
  scopeText: z.string(),
  scope: z.object({
    zones: z.array(z.string()),
    bins: z.array(z.object({ id: zId, code: z.string() })),
    itemGroups: z.array(z.object({ id: zId, name: z.string() })),
    items: z.array(z.object({ id: zId, sku: z.string(), name: z.string() })),
  }),
  /** wer zählen soll – und wie viele Plätze jede Person schon gezählt hat */
  counters: z.array(z.object({ id: zId, name: z.string(), zones: z.array(z.string()), binsCounted: z.int(), planned: z.boolean() })),
  note: z.string().nullable(),
  createdBy: zPerson.nullable(),
  createdAt: z.date(),
  /** Zeitpunkt der Momentaufnahme des Buchbestands */
  startedAt: z.date().nullable(),
  approvedBy: zPerson.nullable(),
  approvedAt: z.date().nullable(),
  version: z.int(),
  progress: zProgress,
  zones: z.array(z.object({ zone: z.string(), binsTotal: z.int(), binsCounted: z.int(), recountsOpen: z.int() })),
  /** darf der Nutzer Buchbestand und Differenzen dieser Inventur sehen (Blindzählung: nur Planung und Freigabe) */
  seesBook: z.boolean(),
  /** Zahl der Positionen mit Abweichung – null, solange der Nutzer sie nicht sehen darf */
  differenceCount: z.int().nullable(),
  /** Summe der Beträge aller Differenzen zum gleitenden Durchschnitt in Cent – nur mit „Einstandspreise sehen“ */
  differenceValueCents: z.int().optional(),
  allowed: z.object({
    edit: z.boolean(),
    start: z.boolean(),
    count: z.boolean(),
    review: z.boolean(),
    recount: z.boolean(),
    approve: z.boolean(),
    cancel: z.boolean(),
  }),
  /** warum der Nutzer (noch) nicht freigeben kann: offene Plätze, Grenze, Vier-Augen-Regel */
  approveHint: z.string().nullable(),
  /** was als Nächstes ansteht, in einem Satz */
  hint: z.string(),
})
export type CountView = z.output<typeof zCountView>

export const zCountRow = z.object({
  id: zId,
  number: z.string().nullable(),
  name: z.string(),
  kind: z.enum(COUNT_KINDS),
  kindLabel: z.string(),
  status: z.enum(COUNT_STATUSES),
  statusLabel: z.string(),
  locationId: zId,
  locationCode: z.string(),
  locationName: z.string(),
  keyDate: z.string(),
  blind: z.boolean(),
  scopeText: z.string(),
  binsTotal: z.int(),
  binsCounted: z.int(),
  recountsOpen: z.int(),
  createdByName: z.string().nullable(),
  startedAt: z.date().nullable(),
})

export const countList = defineContract({
  name: 'stocktaking.count.list',
  title: 'Inventuren anzeigen',
  description: 'Zählpläne und Inventuren mit Verfahren, Stichtag, Umfang, Zustand und Fortschritt.',
  llm: {
    description:
      'Listet Inventuren (Zählpläne) der Standorte, die der Nutzer sehen darf: Verfahren (`full` Stichtag, `rolling` ' +
      'permanent, `spot` Kontrollzählung), Stichtag, Umfang, Zustand (Geplant, Zählung läuft, In Prüfung, Gebucht, ' +
      'Abgeschlossen, Abgebrochen) und Fortschritt in Lagerplätzen. Verwenden für „Welche Inventur läuft?“, „Wie weit ' +
      'ist die Zählung in Neufahrn?“. Für eine Inventur im Einzelnen stocktaking.count.get, für die Abweichungen ' +
      'stocktaking.count.differences. Ändert nichts.',
    keywords: ['Inventur', 'Zählung', 'Zählplan', 'Stichtag', 'permanente Inventur', 'Kontrollzählung', 'Fortschritt'],
  },
  input: listInput({
    sortBy: ['keyDate', 'name', 'status'],
    defaultSort: { by: 'keyDate', dir: 'desc' },
    filters: {
      locationId: zId.optional(),
      status: z.enum(COUNT_STATUSES).optional(),
      kind: z.enum(COUNT_KINDS).optional(),
      /** nur laufende (Geplant, Zählung läuft, In Prüfung) */
      open: z.boolean().optional(),
    },
  }),
  output: listOutput(zCountRow),
  permission: READ,
  risk: 'read',
  scope: 'any',
})

export const countGet = defineContract({
  name: 'stocktaking.count.get',
  title: 'Inventur anzeigen',
  description: 'Eine Inventur mit Plan, Fortschritt je Zone, Zählenden und dem, was als Nächstes ansteht.',
  llm: {
    description:
      'Liefert eine Inventur: Verfahren, Stichtag, Umfang, Blindzählung ja/nein, wer zählt, Fortschritt je Zone ' +
      '(Lagerplätze gezählt von gesamt), offene Nachzählungen, Zahl der Abweichungen und – nur mit dem Recht ' +
      '„Einstandspreise sehen“ – ihren Wert in Cent (`differenceValueCents`, Summe der Beträge zum gleitenden ' +
      'Durchschnitt). `hint` sagt, was ansteht, `approveHint`, warum der Nutzer nicht freigeben kann. Bei einer ' +
      'Blindzählung sind Abweichungen nur für Planung und Freigabe sichtbar (`seesBook`). Ändert nichts.',
    keywords: ['Inventur', 'Fortschritt', 'Zählung', 'Differenzen', 'Freigabe'],
  },
  input: z.strictObject({ id: zId }),
  output: zCountView,
  permission: READ,
  risk: 'read',
  scope: 'record',
  fieldPermissions: { differenceValueCents: PERMISSION_COSTS_READ },
})

export const countPlan = defineContract({
  name: 'stocktaking.count.plan',
  title: 'Inventur planen',
  description: 'Legt einen Zählplan an oder ändert ihn, solange nicht gezählt wird: Standort, Verfahren, Stichtag, Umfang, Zählende.',
  llm: {
    description:
      'Legt einen Zählplan an (ohne `id`) oder ändert einen Plan im Zustand „Geplant“ (mit `id`). `kind`: `full` = ' +
      'Stichtagsinventur, `rolling` = Teilzählung der permanenten Inventur, `spot` = Kontrollzählung auffälliger ' +
      'Artikel. Umfang: ohne `zones` und `binIds` der ganze Standort, sonst die genannten Zonen (Anfang des ' +
      'Lagerplatz-Codes, z. B. „Gang“, „BL“) oder Lagerplätze; ohne `itemGroupIds` und `itemIds` alle Artikel, sonst ' +
      'die Warengruppen oder Artikel. `blind` (Standard an): Zählende sehen keinen Buchbestand. `lockScope`: sperrt ' +
      'die Lagerplätze für andere Buchungen, solange gezählt wird (Standard aus – das Lager arbeitet weiter). ' +
      '`counters`: wer zählt, auf Wunsch je Person die Zonen. Es wird nichts gezählt und nichts gebucht; starten mit ' +
      'stocktaking.count.start. Nicht zum Erfassen von Zählmengen.',
    keywords: ['Inventur planen', 'Zählplan', 'Stichtag', 'Blindzählung', 'Zone', 'Kontrollzählung', 'permanente Inventur'],
  },
  input: z.strictObject({
    id: zId.optional(),
    locationId: zId,
    name: z.string().trim().min(3, { error: 'Bitte einen Namen mit mindestens 3 Zeichen eingeben.' }).max(120).optional(),
    kind: z.enum(COUNT_KINDS).default('full'),
    /** Stichtag; Standard: heute */
    keyDate: zDay.optional(),
    blind: z.boolean().default(true),
    lockScope: z.boolean().default(false),
    zones: z.array(zZone).max(100).default([]),
    binIds: z.array(zId).max(1000).default([]),
    itemGroupIds: z.array(zId).max(200).default([]),
    itemIds: z.array(zId).max(500).default([]),
    counters: z
      .array(z.strictObject({ userId: zId, zones: z.array(zZone).max(100).default([]) }))
      .max(50)
      .default([]),
    note: z.string().trim().max(2000).nullish(),
  }),
  output: zCountView,
  permission: CREATE,
  risk: 'write',
  scope: (input) => [input.locationId],
})

export const countStart = defineContract({
  name: 'stocktaking.count.start',
  title: 'Zählung starten',
  description: 'Hält den Buchbestand des Umfangs als Momentaufnahme fest und gibt die Zählliste frei.',
  llm: {
    description:
      'Startet eine geplante Inventur: Der Buchbestand jedes Lagerplatzes im Umfang wird als Momentaufnahme ' +
      'festgehalten, die Zählenden bekommen ihre Zählliste. Am Bestand ändert sich nichts. Mit `lockScope` sind die ' +
      'Lagerplätze danach für andere Buchungen gesperrt, bis die Inventur gebucht oder abgebrochen ist. Nur im ' +
      'Zustand „Geplant“.',
    keywords: ['Inventur starten', 'Zählung starten', 'Zählliste freigeben'],
  },
  input: z.strictObject({ id: zId }),
  output: zCountView,
  permission: CREATE,
  risk: 'write',
  scope: 'record',
})

export const countCancel = defineContract({
  name: 'stocktaking.count.cancel',
  title: 'Inventur abbrechen',
  description: 'Bricht eine Inventur ab, die noch nicht gebucht ist. Gebucht wird nichts; die Zählungen bleiben im Protokoll.',
  llm: {
    description:
      'Bricht eine Inventur vor dem Buchen ab (mit Grund). Ein Plan ohne Zählung wird verworfen, eine begonnene ' +
      'Inventur bleibt mit ihren Zählungen als „Abgebrochen“ stehen; eine Sperre des Umfangs endet. Am Bestand ' +
      'ändert sich nichts. Eine gebuchte Inventur lässt sich nicht abbrechen.',
    keywords: ['Inventur abbrechen', 'Zählung abbrechen', 'verwerfen'],
  },
  input: z.strictObject({ id: zId, reason: zReason }),
  output: z.object({ id: zId, status: z.enum(COUNT_STATUSES), statusLabel: z.string(), discarded: z.boolean() }),
  permission: CREATE,
  risk: 'destructive',
  scope: 'record',
})

/** Was sich an einem Standort planen lässt: Zonen und Lagerplätze mit ihrem Bestand, mögliche Zählende, Warengruppen. */
export const countScopeOptions = defineContract({
  name: 'stocktaking.count.scopeOptions',
  title: 'Umfang einer Inventur vorschlagen',
  description: 'Zonen und Lagerplätze eines Standorts, die Personen mit dem Recht zu zählen und die Warengruppen.',
  llm: {
    description:
      'Liefert für einen Standort, was ein Zählplan nennen kann: Zonen (Anfang des Lagerplatz-Codes) mit der Zahl ' +
      'ihrer Lagerplätze und Positionen, die Lagerplätze, die Personen, die dort zählen dürfen (`counters`), und die ' +
      'Warengruppen. Vor stocktaking.count.plan verwenden, um Zonen und Personen richtig zu benennen. Ändert nichts.',
    keywords: ['Zonen', 'Lagerplätze', 'Zählende', 'Inventur planen'],
  },
  input: z.strictObject({ locationId: zId }),
  output: z.object({
    location: zLocationRef,
    zones: z.array(z.object({ zone: z.string(), bins: z.int(), positions: z.int() })),
    bins: z.array(z.object({ id: zId, code: z.string(), name: z.string().nullable(), zone: z.string(), positions: z.int() })),
    counters: z.array(zPerson),
    itemGroups: z.array(z.object({ id: zId, name: z.string() })),
  }),
  permission: CREATE,
  risk: 'read',
  scope: (input) => [input.locationId],
})

/** Auffällige Artikel für eine Kontrollzählung: Minusbestand, lange ohne Bewegung. */
export const countSuspects = defineContract({
  name: 'stocktaking.count.suspects',
  title: 'Auffällige Artikel für eine Kontrollzählung',
  description: 'Artikel eines Standorts mit Minusbestand oder ohne Bewegung seit vielen Tagen.',
  llm: {
    description:
      'Schlägt Artikel für eine Kontrollzählung vor: Bestand im Minus (`negative`) oder Bestand, der seit ' +
      '`unmovedDays` Tagen (Standard 90) nicht bewegt wurde (`unmoved`). Die Kennungen passen in `itemIds` von ' +
      'stocktaking.count.plan mit `kind: spot`. Ändert nichts.',
    keywords: ['Kontrollzählung', 'Minusbestand', 'Ladenhüter', 'ohne Bewegung', 'auffällig', 'verdächtig'],
  },
  input: z.strictObject({
    locationId: zId,
    unmovedDays: z.int().min(7, { error: 'Mindestens 7 Tage.' }).max(730).default(90),
  }),
  output: z.object({
    rows: z.array(
      z.object({
        item: zItemRef,
        cause: z.enum(['negative', 'unmoved']),
        causeText: z.string(),
        binCodes: z.array(z.string()),
        lastMovementAt: z.date().nullable(),
        lastCountedAt: z.date().nullable(),
      }),
    ),
  }),
  permission: CREATE,
  risk: 'read',
  scope: (input) => [input.locationId],
})
